import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { PhotoIcon, CurrencyDollarIcon, ListBulletIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import ru from 'date-fns/locale/ru';

registerLocale('ru', ru);

const DishForm = ({ onFormSubmit, isEditing = false, initialData = null }) => {
    const { authFetch } = useUser();
    const navigate = useNavigate();
    
    const [name, setName] = useState(initialData?.name || '');
    const [price, setPrice] = useState(initialData?.price || '');
    const [ingredients, setIngredients] = useState(initialData?.ingredients || '');
    const [dishClass, setDishClass] = useState(initialData?.dishClass || '');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(initialData?.imageUrl || null);
    
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [availableDates, setAvailableDates] = useState(() => {
        try {
          return initialData?.days?.map(d => {
            const date = new Date(d);
            return isNaN(date.getTime()) ? null : date;
          }).filter(Boolean) || [];
        } catch {
          return [];
        }
      });
      const [hasGarnir, setHasGarnir] = useState(() =>
        initialData?.hasGarnir ? true : false
      );
      
      const [isRepeating, setIsRepeating] = useState(false);
      const [repeatStatus, setRepeatStatus] = useState({ message: null, type: null });

    useEffect(() => {
        if (isEditing && initialData) {
          setName(initialData.name || '');
          setPrice(initialData.price || '');
          setIngredients(initialData.ingredients || '');
          setDishClass(initialData.dishClass || '');
          setImagePreview(initialData.imageUrl || null);
          setImageFile(null);
          setAvailableDates(
            (initialData.days || [])
              .map(d => {
                const date = new Date(d);
                return isNaN(date.getTime()) ? null : date;
              })
              .filter(Boolean)
          );
          setHasGarnir(!!initialData.hasGarnir);
        }
      
        if (!isEditing) {
          setName('');
          setPrice('');
          setIngredients('');
          setDishClass('');
          setImageFile(null);
          setImagePreview(null);
          setAvailableDates([]);
          setHasGarnir(false);
        }
      }, [isEditing, initialData]);
      
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setSuccess(false);

        if (!isEditing && !imageFile) {
            setError('Фотография блюда обязательна при создании.');
            setIsLoading(false);
            return;
        }
        if (!name || price === '' || price < 0) {
            setError('Название и корректная цена обязательны.');
            setIsLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('name', name);
        formData.append('price', parseFloat(price));
        formData.append('ingredients', ingredients);
        formData.append('dishClass', dishClass);
        if (imageFile) {
            formData.append('image', imageFile);
        }
        const formattedDates = availableDates.map(date => date.toISOString().split('T')[0]);
formData.append('days', JSON.stringify(formattedDates));
formData.append('hasGarnir', hasGarnir);


        const url = isEditing ? `/dishes/${initialData.id}` : '/dishes';
        const method = isEditing ? 'PATCH' : 'POST';

        try {
            const responseData = await authFetch(url, {
                method: method,
                body: formData,
            });
            
            setSuccess(true);
            setImageFile(null);
            if (document.getElementById('file-upload')) {
                document.getElementById('file-upload').value = null;
            }
            if (!isEditing) {
                setName('');
                setPrice('');
                setIngredients('');
                setDishClass('');
                setImagePreview(null);
            } else {
                 if (responseData?.imageUrl) {
                    setImagePreview(responseData.imageUrl);
                 }
            }

            if (onFormSubmit) {
                onFormSubmit(responseData);
            }
            
            setTimeout(() => setSuccess(false), 3000);

        } catch (err) {
            console.error(`Failed to ${isEditing ? 'update' : 'add'} dish:`, err);
            setError(err.message || `Не удалось ${isEditing ? 'обновить' : 'добавить'} блюдо. Попробуйте снова.`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRepeatDish = async () => {
        if (!initialData?.id) return;
    
        if (!window.confirm('Повторить даты для этого блюда? Будут добавлены новые даты (+14 дней), а прошедшие — удалены.')) {
            return;
        }
    
        setIsRepeating(true);
        setRepeatStatus({ message: null, type: null });
    
        try {
            const responseData = await authFetch(`/dishes/${initialData.id}/repeat`, {
                method: 'POST',
            });
            
            setRepeatStatus({ message: responseData.message || 'Даты успешно повторены!', type: 'success' });
            
            // Обновляем даты прямо в форме, чтобы изменения были видны сразу
            if (responseData.dish?.days) {
                const newDates = responseData.dish.days.map(d => {
                    const date = new Date(d.replace(/-/g, '/'));
                    return isNaN(date.getTime()) ? null : date;
                }).filter(Boolean);
                setAvailableDates(newDates);
            }

            // Оповещаем родительский компонент, чтобы он тоже обновил данные
            if (onFormSubmit) {
                onFormSubmit(responseData.dish);
            }
    
        } catch (err) {
            console.error('Failed to repeat dish:', err);
            setRepeatStatus({ message: err.message || 'Не удалось повторить блюдо.', type: 'error' });
        } finally {
            setIsRepeating(false);
            setTimeout(() => setRepeatStatus({ message: null, type: null }), 4000);
        }
    };

    return (
        <div className="text-gray-200 pt-4 pb-8">
            <div className="w-full max-w-2xl mx-auto p-6 md:p-8 space-y-6 bg-gray-800 bg-opacity-90 backdrop-filter backdrop-blur-sm rounded-xl shadow-2xl">
                <h2 className="text-2xl font-semibold text-white mb-6">
                    {isEditing ? 'Редактировать блюдо' : 'Добавить новое блюдо'}
                </h2>

                {repeatStatus.message && (
                    <div className={`p-3 mb-4 rounded-md text-sm flex items-center gap-2 ${repeatStatus.type === 'success' ? 'bg-green-900 bg-opacity-70 text-green-700' : 'bg-red-900 bg-opacity-70 text-red-700'}`}>
                        {repeatStatus.type === 'success' ? '✅' : '❌'}
                        <span>{repeatStatus.message}</span>
                    </div>
                )}
                {error && (
                    <div className="p-3 bg-red-900 bg-opacity-70 border border-red-700 rounded-md text-red-300 text-sm flex items-center gap-2">
                       <XCircleIcon className="h-5 w-5 flex-shrink-0"/> <span>{error}</span>
                    </div>
                 )}
                {success && (
                     <div className="p-3 bg-green-900 bg-opacity-70 border border-green-700 rounded-md text-green-300 text-sm flex items-center gap-2">
                        <CheckCircleIcon className="h-5 w-5 flex-shrink-0"/> <span>{`Блюдо успешно ${isEditing ? 'обновлено' : 'добавлено'}!`}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label htmlFor="dish-photo" className="block text-sm font-medium text-gray-400 mb-1">Фотография {isEditing ? '(оставьте пустым, чтобы не менять)' : ''}</label>
                        <div className="mt-1 flex items-center gap-4">
                            <span className="inline-block h-20 w-20 rounded-md overflow-hidden bg-gray-700 flex items-center justify-center">
                                {imagePreview ? (
                                    <img src={imagePreview} alt="Предпросмотр" className="h-full w-full object-cover" />
                                ) : (
                                    <PhotoIcon className="h-10 w-10 text-gray-500" aria-hidden="true" />
                                )}
                            </span>
                            <label htmlFor="file-upload" className="relative cursor-pointer bg-gray-600 hover:bg-gray-500 text-white py-1.5 px-3 border border-gray-500 rounded-md shadow-sm text-xs font-medium focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-offset-gray-800 focus-within:ring-blue-500">
                                <span>{imagePreview ? 'Изменить' : 'Загрузить фото'}</span>
                                <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleImageChange} accept="image/*" />
                            </label>
                             {imagePreview && (
                                <button type="button" onClick={() => {setImagePreview(null); setImageFile(null); if(document.getElementById('file-upload')) document.getElementById('file-upload').value = null;}} className="text-xs text-red-400 hover:text-red-300">
                                    Удалить {imageFile ? 'новый файл' : 'текущее фото'}
                                </button>
                            )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{isEditing ? 'Загрузите новый файл, чтобы заменить текущее изображение.' : 'Фото обязательно.'}</p>
                    </div>

                    <div>
                        <label htmlFor="dish-name" className="block text-sm font-medium text-gray-400 mb-1">Название блюда <span className="text-red-400">*</span></label>
                        <input
                            type="text"
                            id="dish-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className="block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>

                    <div>
                        <label htmlFor="dish-price" className="block text-sm font-medium text-gray-400 mb-1">Цена (руб.) <span className="text-red-400">*</span></label>
                        <div className="relative rounded-md shadow-sm">
                             <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center">
                                <CurrencyDollarIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
                            </div>
                            <input
                                type="number"
                                id="dish-price"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                required
                                min="0"
                                step="0.01"
                                className="block w-full bg-gray-700 border border-gray-600 rounded-md py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="0.00"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="dish-class" className="block text-sm font-medium text-gray-400 mb-1">Класс блюда</label>
                        <select
                            id="dish-class"
                            value={dishClass}
                            onChange={(e) => setDishClass(e.target.value)}
                            className="block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                            <option value="">Не выбрано</option>
                            <option value="первое">Первое</option>
                            <option value="второе">Второе</option>
                            <option value="салат">Салат</option>
                            <option value="гарнир">Гарнир</option>
                            <option value="напиток">Напиток</option>
                        </select>
                    </div>
                    {dishClass === 'второе' && (
  <div>
    <label className="block text-sm font-medium text-gray-400 mb-1">
      Идет ли к блюду гарнир?
    </label>
    <select
      value={hasGarnir ? 'yes' : 'no'}
      onChange={(e) => setHasGarnir(e.target.value === 'yes')}
      className="block w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
    >
      <option value="no">Нет</option>
      <option value="yes">Да</option>
    </select>
  </div>
)}

                    <div className="mb-4">
  <label className="block text-sm font-medium text-gray-200 mb-2">
    Даты доступности блюда:
  </label>
  <DatePicker
    selected={null}
    onChange={(date) => {
      const exists = availableDates.find(d => d.toDateString() === date.toDateString());
      if (exists) {
        setAvailableDates(availableDates.filter(d => d.toDateString() !== date.toDateString()));
      } else {
        setAvailableDates([...availableDates, date]);
      }
    }}
    highlightDates={availableDates.map(d => new Date(d))}
    locale="ru"
    inline
    dateFormat="dd.MM.yyyy"
    placeholderText="Выберите дату"
  />
  <p className="text-sm text-gray-400 mt-1">
    Клик по дате — добавляет или убирает её.
  </p>
</div>

                    <div>
                        <label htmlFor="dish-ingredients" className="block text-sm font-medium text-gray-400 mb-1">Состав / Описание</label>
                         <div className="relative rounded-md shadow-sm">
                             <div className="pointer-events-none absolute inset-y-0 left-0 top-0 pt-2 pl-3 flex items-start">
                                <ListBulletIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
                            </div>
                            <textarea
                                id="dish-ingredients"
                                value={ingredients}
                                onChange={(e) => setIngredients(e.target.value)}
                                rows={3}
                                className="block w-full bg-gray-700 border border-gray-600 rounded-md py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="Перечислите ингредиенты или дайте краткое описание..."
                            />
                        </div>
                    </div>

                    <div className="pt-2 flex items-center gap-4">
                        <button
                            type="submit"
                            disabled={isLoading || isRepeating}
                            className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${isLoading || isRepeating ? 'bg-gray-500 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500'}`}
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Сохранение...
                                </> 
                             ) : (
                                isEditing ? 'Сохранить изменения' : 'Добавить блюдо'
                            )}
                        </button>
                        
                        {isEditing && (
                            <button
                                type="button"
                                onClick={handleRepeatDish}
                                disabled={isRepeating || isLoading}
                                title="Повторить даты (+14 дней) и очистить прошедшие"
                                className={`flex-shrink-0 flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${isRepeating ? 'bg-gray-500 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-teal-500'}`}
                            >
                                {isRepeating ? 'Повтор...' : 'Повторить'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DishForm; 